/**
 * PolicyEditor Component
 * 
 * This is the main component for creating and editing Azure Policies. It provides
 * a complete interface for policy management, including:
 * - Basic policy information (name, description, etc.)
 * - Condition configuration with logical operators
 * - Parameter management
 * - Policy effect settings
 * - Live JSON preview
 * - Resizable split view
 */

import React, { useState } from 'react';
import { useParameters, usePolicyEditorState, useEditorLayout } from '.';
import { Parameters } from '../../types/policyEditor';
import { BasicInfoTab } from './components/BasicInfoTab';
import { ConditionsTab } from './components/ConditionsTab';
import { ParametersTab } from './components/ParametersTab';
import { DetailsTab } from './components/DetailsTab';
import { ExistenceConditionTab } from './components/ExistenceConditionTab';
import { DeploymentTab } from './components/DeploymentTab';
import { TabNavigation } from './components/TabNavigation';
import { ResizeHandle } from './components/ResizeHandle';
import { PreviewSection } from './components/PreviewSection';
import { AboutTab } from './components/AboutTab';
import ImportPolicyModal from './components/ImportPolicyModal';
import AliasSearch from './components/AliasSearch/AliasSearch';
import '../../styles/scrollbars.css';

/**
 * PolicyEditor Component
 * 
 * Provides a comprehensive interface for Azure Policy creation and editing.
 * Uses custom hooks for state management and layout control.
 * 
 * @example
 * <PolicyEditor />
 */
const PolicyEditor: React.FC = () => {
  // Initialize policy state management
  const {
    policy,
    setPolicy,
    selectedEffect,
    activeTab,
    setActiveTab,
    handleDisplayNameChange,
    handleDescriptionChange,
    handleCategoryChange,
    handleModeChange,
    handleEffectChange,
    handleEditorChange,
    handleUpdateDetails,
    handleUpdateParameter,
    handleRemoveParameter: removeParameterFromPolicy,
    importPolicy
  } = usePolicyEditorState();
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Initialize layout management
  const {
    isDragging,               // Resize drag state
    splitPosition,            // Split position percentage
    editorRef,                // Reference to editor container
    handleMouseDown           // Resize initiation handler
  } = useEditorLayout();

  // Convert PolicyParameter to Parameters type for the hook
  const parametersForHook: Parameters = Object.entries(policy.properties.parameters).reduce((acc, [key, param]) => {
    acc[key] = {
      type: param.type,
      metadata: {
        displayName: param.metadata?.displayName || '',
        description: param.metadata?.description || ''
      },
      allowedValues: param.allowedValues || [],
      defaultValue: String(param.defaultValue || '')
    };
    return acc;
  }, {} as Parameters);

  // Extract parameter management functionality from custom hook
  const {
    parameterForm,             // Current parameter form state
    editingParameter,         // Currently editing parameter
    nameError,                // Parameter name validation error
    allowedValuesError,       // Allowed values validation error
    defaultValueError,        // Default value validation error
    handleParameterFormChange, // Form field update handler
    handleAddParameter,       // Parameter addition handler
    handleEditParameter,       // Parameter edit handler
    handleCancelEdit,          // Cancel parameter edit
    updateEditingParameterName // Update editing parameter name without resetting form
  } = useParameters(parametersForHook);

  // Preview visibility state
  const [isPreviewVisible, setIsPreviewVisible] = useState(true);

  // Toggle preview visibility
  const togglePreview = () => {
    setIsPreviewVisible(!isPreviewVisible);
  };

  // New: create and edit an empty parameter on demand
  const handleAddClick = () => {
    // generate a unique parameter key
    const existingKeys = Object.keys(policy.properties.parameters);
    let index = existingKeys.length + 1;
    let newKey = `param${index}`;
    while (existingKeys.includes(newKey)) {
      index++;
      newKey = `param${index}`;
    }
    // default empty parameter object
    const emptyParam = {
      type: 'string',
      metadata: { displayName: '', description: '' },
      allowedValues: [],
      defaultValue: ''
    };
    // insert into policy and open form for editing
    handleUpdateParameter(newKey, emptyParam);
    handleEditParameter(newKey, emptyParam);
    setActiveTab('parameters');
  };

  // New: handler for live parameter updates during editing
  const handleLiveParameterUpdate = (parameterKey: string, parameter: any) => {
    // Simply update the parameter at the given key - no key changes during live editing
    handleUpdateParameter(parameterKey, parameter);
  };

  // Handle parameter name changes when done editing
  const handleParameterSave = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If the name changed, handle renaming
    if (editingParameter && parameterForm.name !== editingParameter) {
      // Remove old parameter and add with new name
      const newParameters = { ...policy.properties.parameters };
      const currentParam = newParameters[editingParameter];
      delete newParameters[editingParameter];
      
      // Update the parameter with the new name from the form
      const updatedParam = {
        ...currentParam,
        metadata: {
          ...currentParam.metadata,
          displayName: parameterForm.displayName,
          description: parameterForm.description
        }
      };
      
      setPolicy(prev => ({
        ...prev,
        properties: {
          ...prev.properties,
          parameters: {
            ...newParameters,
            [parameterForm.name]: updatedParam
          }
        }
      }));
    }
    
    // Exit editing mode
    handleCancelEdit();
  };

  return (
    <div className="flex items-stretch justify-between gap-0 overflow-hidden w-full h-screen min-h-0 font-mono bg-white dark:bg-gray-800" ref={editorRef}>
      {/* Main editor section - now horizontal layout with side panel */}
      <div className="flex flex-none min-w-[350px]" style={{ flex: isPreviewVisible ? `0 0 ${splitPosition}%` : '1 1 100%' }}>
        {/* Side panel navigation */}
        <TabNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          selectedEffect={selectedEffect}
          isPreviewVisible={isPreviewVisible}
          togglePreview={togglePreview}
        />

        {/* Tab Content Area - main content section */}
        <div className="flex-grow overflow-auto p-3 border-r border-gray-300 dark:border-gray-600"> 
          {/* Basic Info Tab */}
          {activeTab === 'basicInfo' && (
            <BasicInfoTab
              policy={policy}
              selectedEffect={selectedEffect}
              onDisplayNameChange={handleDisplayNameChange}
              onDescriptionChange={handleDescriptionChange}
              onCategoryChange={handleCategoryChange}
              onModeChange={handleModeChange}
              onEffectChange={handleEffectChange}
            />
          )}

          {/* Conditions Tab */}
          {activeTab === 'conditions' && (
            <ConditionsTab
              policy={policy}
              onUpdatePolicy={setPolicy}
            />
          )}

          {/* Parameters Tab */}
          {activeTab === 'parameters' && (
            <ParametersTab
              policy={policy}
              parameterForm={parameterForm}
              editingParameter={editingParameter}
              nameError={nameError}
              allowedValuesError={allowedValuesError}
              defaultValueError={defaultValueError}
              onParameterFormChange={handleParameterFormChange}
              onAddClick={handleAddClick}
              onAddParameter={handleParameterSave}
              onEditParameter={handleEditParameter}
              onRemoveParameter={removeParameterFromPolicy}
              onCancelEdit={handleCancelEdit}
              onLiveParameterUpdate={handleLiveParameterUpdate}
            />
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (selectedEffect.toLowerCase() === 'modify' || selectedEffect.toLowerCase() === 'auditifnotexists' || selectedEffect.toLowerCase() === 'deployifnotexists') && (
            <DetailsTab
              policy={policy}
              selectedEffect={selectedEffect}
              onUpdateDetails={handleUpdateDetails}
            />
          )}

          {/* Existence Condition Tab - only for auditIfNotExists and deployIfNotExists effects */}
          {activeTab === 'existenceCondition' && (selectedEffect.toLowerCase() === 'auditifnotexists' || selectedEffect.toLowerCase() === 'deployifnotexists') && (
            <ExistenceConditionTab
              policy={policy}
              onUpdatePolicy={setPolicy}
            />
          )}

          {/* Deployment Tab - only for deployIfNotExists effect */}
          {activeTab === 'deployment' && selectedEffect.toLowerCase() === 'deployifnotexists' && (
            <DeploymentTab
              policy={policy}
              onUpdatePolicy={setPolicy}
            />
          )}

          {/* About Tab */}
          {activeTab === 'about' && <AboutTab />}
          {activeTab === 'aliasSearch' && <AliasSearch />}
          
          {/* Import Tab */}
          {activeTab === 'import' && (
            <div className="max-w-4xl mx-auto">
              {/* Header Section */}
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Import Policy</h1>
                    <p className="text-gray-600 dark:text-gray-400">Import existing Azure Policy definitions to edit or use as templates</p>
                  </div>
                </div>
              </div>

              {/* Import Options */}
              <div className="grid md:grid-cols-1 gap-6">
                {/* Main Import Card */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Import from JSON</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Upload a JSON file or paste policy content directly</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <button
                        className="w-full flex items-center justify-center space-x-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                        onClick={() => setIsImportOpen(true)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        <span className="font-medium">Open Import Editor</span>
                      </button>

                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        Supports Azure Policy JSON format with automatic validation
                      </div>
                    </div>
                  </div>
                </div>

                {/* Import Guidelines Card */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-xl border border-blue-200 dark:border-gray-600 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Import Guidelines</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Tips for successful policy imports</p>
                      </div>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-gray-700 dark:text-gray-300">
                          <strong>Supported formats:</strong> Azure Policy JSON with mode, policyRule (required), parameters (optional), and metadata (optional)
                        </p>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <p className="text-gray-700 dark:text-gray-300">
                          <strong>Automatic validation:</strong> JSON structure and required fields are validated on import
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resize handle - only show when preview is visible */}
      {isPreviewVisible && (
        <ResizeHandle 
          isDragging={isDragging}
          onMouseDown={handleMouseDown}
        />
      )}

      {/* JSON preview section - only show when preview is visible */}
      {isPreviewVisible && (
        <PreviewSection 
          policy={policy}
          splitPosition={splitPosition}
        />
      )}

      {/* Import Policy Modal */}
      <ImportPolicyModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        importPolicy={importPolicy}
      />
    </div>
  );
};

export default PolicyEditor;
